/**
 * @module components/Users/UserManagementModal
 * @description Modal de gestión de usuarios del sistema (solo administradores).
 *
 * Permite a los administradores crear, editar y desactivar usuarios de la organización.
 * Gestiona los roles (admin, technical_referent, auditor, viewer), unidades organizacionales
 * y el flag `force_password_change` para el primer login.
 *
 * La creación de usuarios invoca la función `AuthContext.createUser()` que usa la
 * Service Role Key de Supabase (no la anon key) para crear usuarios sin iniciar sesión.
 *
 * @see AuthContext para las funciones createUser(), updateUser() y deactivateUser()
 * @see GlobalSidebar que incluye el acceso a este modal para administradores
 */
import React, { useState, useEffect } from 'react';
import {
  X,
  Users,
  UserPlus,
  Mail,
  Building2,
  Shield,
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';

// ─── Tipos ───────────────────────────────────────────────────────────────────

type UserRole = 'admin' | 'technical_referent' | 'auditor' | 'viewer';

interface OrgUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  unit: string;
  is_active: boolean;
  created_at?: string;
}

interface UserManagementModalProps {
  onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrador',
  technical_referent: 'Referente Técnico',
  auditor: 'Auditor',
  viewer: 'Observador',
};

const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  technical_referent: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  auditor: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  viewer: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
};

const ROLE_ICONS: Record<UserRole, React.ElementType> = {
  admin: Shield,
  technical_referent: UserCheck,
  auditor: Eye,
  viewer: Eye,
};

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  role: 'viewer' as UserRole,
  unit: '',
};

// ─── Componente principal ─────────────────────────────────────────────────────

export function UserManagementModal({ onClose }: UserManagementModalProps) {
  const { user: currentUser } = useAuth();
  const { technicalReferents, enableAdminAsReferent, disableAdminAsReferent } = useData();
  const [togglingReferent, setTogglingReferent] = useState<string | null>(null);

  const [users, setUsers] = useState<OrgUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // ── Cargar usuarios de la organización ──────────────────────────────────────
  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setIsLoading(true);
    try {
      // Modo demo: generar usuarios mock
      if (!currentUser?.organization_id || currentUser.organization_id === 'default-org') {
        setUsers([
          {
            id: 'demo-1',
            name: currentUser?.name ?? 'Admin Demo',
            email: currentUser?.email ?? 'admin@company.com',
            role: 'admin',
            unit: currentUser?.unit ?? 'TI',
            is_active: true,
          },
        ]);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, role, unit, is_active, created_at')
        .eq('organization_id', currentUser.organization_id)
        .order('name');

      if (error) throw error;
      setUsers((data as OrgUser[]) ?? []);
    } catch (err) {
      console.error('[USERS] ❌ Error cargando usuarios:', err);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }

  // ── Crear usuario ────────────────────────────────────────────────────────────
  async function handleAddUser(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) return;

    setIsSaving(true);
    setFeedback(null);

    try {
      // Modo demo
      if (!currentUser?.organization_id || currentUser.organization_id === 'default-org') {
        await new Promise((r) => setTimeout(r, 600));
        setUsers((prev) => [
          ...prev,
          {
            id: `demo-${Date.now()}`,
            name: form.name,
            email: form.email,
            role: form.role,
            unit: form.unit,
            is_active: true,
          },
        ]);
        setFeedback({ type: 'success', message: `Usuario ${form.name} agregado (modo demo).` });
        setForm(EMPTY_FORM);
        setShowAddForm(false);
        return;
      }

      // 0. Guardar sesión del admin actual ANTES de cualquier operación Auth
      const { data: { session: adminSession } } = await supabase.auth.getSession();
      if (!adminSession) throw new Error('No hay sesión activa de administrador');

      // 1. Crear usuario en Supabase Auth
      // NOTA: signUp dispara SIGNED_IN en onAuthStateChange — restauramos la sesión admin justo después
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: {
          data: {
            name: form.name.trim(),
            role: form.role,
            organization_id: currentUser.organization_id,
            unit: form.unit.trim(),
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('No se pudo crear el usuario en Auth');

      // 2. Restaurar sesión del admin INMEDIATAMENTE para evitar que el header cambie
      await supabase.auth.setSession({
        access_token: adminSession.access_token,
        refresh_token: adminSession.refresh_token,
      });

      // 3. Insertar perfil en public.users
      const { error: profileError } = await supabase.from('users').insert({
        id: authData.user.id,
        organization_id: currentUser.organization_id,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        unit: form.unit.trim(),
        is_active: true,
      });

      if (profileError) throw profileError;

      setFeedback({ type: 'success', message: `Usuario ${form.name} creado. Se envió email de confirmación.` });
      setForm(EMPTY_FORM);
      setShowAddForm(false);
      await loadUsers();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      console.error('[USERS] ❌ Error creando usuario:', err);
      setFeedback({ type: 'error', message: msg });
    } finally {
      setIsSaving(false);
    }
  }

  // ── Desactivar usuario ───────────────────────────────────────────────────────
  async function handleToggleActive(userId: string, currentActive: boolean) {
    if (userId === currentUser?.id) return; // No desactivarse a sí mismo

    // Modo demo
    if (!currentUser?.organization_id || currentUser.organization_id === 'default-org') {
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, is_active: !currentActive } : u))
      );
      return;
    }

    try {
      const { error } = await supabase
        .from('users')
        .update({ is_active: !currentActive })
        .eq('id', userId);

      if (error) throw error;
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, is_active: !currentActive } : u))
      );
    } catch (err) {
      console.error('[USERS] ❌ Error actualizando estado:', err);
    }
  }

  // ── Habilitar/deshabilitar admin como referente técnico ──────────────────────
  async function handleToggleAdminReferent(userId: string, isCurrentlyReferent: boolean) {
    setTogglingReferent(userId);
    try {
      if (isCurrentlyReferent) {
        await disableAdminAsReferent(userId);
        setFeedback({ type: 'success', message: 'Administrador removido como referente técnico.' });
      } else {
        await enableAdminAsReferent(userId);
        setFeedback({ type: 'success', message: 'Administrador habilitado como referente técnico asignable.' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Error al actualizar estado de referente.' });
    } finally {
      setTogglingReferent(null);
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Gestión de Usuarios
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {users.length} usuario{users.length !== 1 ? 's' : ''} en tu organización
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Feedback banner */}
        {feedback && (
          <div
            className={`mx-6 mt-4 px-4 py-3 rounded-lg flex items-center gap-2 text-sm ${
              feedback.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
            }`}
          >
            {feedback.type === 'success' ? (
              <CheckCircle className="w-4 h-4 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0" />
            )}
            {feedback.message}
            <button
              onClick={() => setFeedback(null)}
              className="ml-auto text-current opacity-60 hover:opacity-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Contenido principal */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Lista de usuarios */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
              No hay usuarios en esta organización.
            </p>
          ) : (
            <ul className="space-y-2">
              {users.map((u) => {
                const RoleIcon = ROLE_ICONS[u.role] ?? Eye;
                const isSelf = u.id === currentUser?.id;
                const isAdminUser = u.role === 'admin';
                const isAdminReferent = isAdminUser && technicalReferents.some(r => r.id === u.id);
                return (
                  <li
                    key={u.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40"
                  >
                    {/* Avatar */}
                    <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                      <span className="text-white text-sm font-medium">
                        {u.name.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {u.name}
                        </span>
                        {isSelf && (
                          <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                            Tú
                          </span>
                        )}
                        {isAdminReferent && (
                          <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                            Referente
                          </span>
                        )}
                        {!u.is_active && (
                          <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 rounded">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>
                      {u.unit && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{u.unit}</p>
                      )}
                    </div>

                    {/* Rol */}
                    <span
                      className={`hidden sm:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium shrink-0 ${ROLE_COLORS[u.role]}`}
                    >
                      <RoleIcon className="w-3 h-3" />
                      {ROLE_LABELS[u.role]}
                    </span>

                    {/* Toggle referente para admins */}
                    {isAdminUser && (
                      <button
                        onClick={() => handleToggleAdminReferent(u.id, isAdminReferent)}
                        disabled={togglingReferent === u.id}
                        title={isAdminReferent ? 'Quitar como referente técnico' : 'Habilitar como referente técnico asignable'}
                        className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                          isAdminReferent
                            ? 'text-green-600 dark:text-green-400 hover:bg-red-100 dark:hover:bg-red-900/20 hover:text-red-500'
                            : 'text-gray-400 hover:bg-green-100 dark:hover:bg-green-900/20 hover:text-green-600'
                        }`}
                      >
                        {togglingReferent === u.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                      </button>
                    )}

                    {/* Acción activar/desactivar */}
                    {!isSelf && (
                      <button
                        onClick={() => handleToggleActive(u.id, u.is_active)}
                        title={u.is_active ? 'Desactivar usuario' : 'Activar usuario'}
                        className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                          u.is_active
                            ? 'hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500'
                            : 'hover:bg-green-100 dark:hover:bg-green-900/20 text-gray-400 hover:text-green-600'
                        }`}
                      >
                        {u.is_active ? <Trash2 className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Formulario para agregar usuario */}
          {showAddForm && (
            <form
              onSubmit={handleAddUser}
              className="border border-blue-200 dark:border-blue-700 rounded-xl p-4 bg-blue-50 dark:bg-blue-900/10 space-y-3"
            >
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                Nuevo usuario
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Nombre Apellido"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="usuario@org.cl"
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contraseña temporal *
                  </label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Mínimo 8 caracteres"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rol *
                  </label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {(Object.entries(ROLE_LABELS) as [UserRole, string][]).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Unidad / Área
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      type="text"
                      value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      placeholder="Ej: División de TI, Gestión de Riesgos..."
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); setFeedback(null); }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  {isSaving ? 'Creando...' : 'Crear usuario'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Solo administradores pueden gestionar usuarios.
          </p>
          {!showAddForm && (
            <button
              onClick={() => { setShowAddForm(true); setFeedback(null); }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Agregar usuario
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
